import { useEffect } from 'react';

import { TimeService } from '@/utils/services/TimeService';

import { useAlbumContext } from '@/utils/hooks/context/useAlbumContext';
import useDateLimits from '@/utils/hooks/datepicker/useDateLimits';
import { useGroupStories } from '@/utils/hooks/swr/group/useGroupStories';
// Hooks
import useStories from '@/utils/hooks/swr/useStories';
import useActiveGroup from '@/utils/hooks/user/useActiveGroup';
import useActiveSocialProfile from '@/utils/hooks/user/useActiveSocialProfile';
import useIsGroup from '@/utils/hooks/user/useIsGroup';

import MediaSelectionSection from '@/components/pages/albums/AlbumFunnelModal/ModalSteps/ManualStep/components/MediaSelectionSection';
// Components
import PostsList from '@/components/pages/albums/AlbumFunnelModal/ModalSteps/ManualStep/components/PostsList';

const StoriesListWrapper = () => {
    const { albumInfo } = useAlbumContext();
    const { getMinDate, getMaxDate } = useDateLimits();
    const { activeSocialProfile } = useActiveSocialProfile();
    const { activeGroup } = useActiveGroup();
    const isGroup = useIsGroup();

    const startDate = TimeService.formatDateForAPI(
        albumInfo?.criteria?.started_at || getMinDate(activeSocialProfile)
    );
    const endDate = TimeService.formatDateForAPI(
        albumInfo?.criteria?.ended_at || getMaxDate(activeSocialProfile)
    );

    // Group stories hook
    const groupStoriesResult = useGroupStories({
        groupId: isGroup ? activeGroup?.id : null,
        activeGroupProfiles: activeGroup?.social_profiles || [],
        selectedProfileIds: albumInfo?.criteria?.social_profiles || [],
        params: {
            start: startDate,
            end: endDate,
            filter_labels: albumInfo?.criteria?.labels || [],
            filter_hashtags: albumInfo?.criteria?.hashtags || [],
            filter_post_type: albumInfo?.criteria?.post_types || []
        },
        options: {
            enabled: isGroup,
            revalidateOnFocus: false,
            revalidateOnReconnect: false
        }
    });

    // Single SP stories hook
    const singleSPResult = useStories({
        activeSPID: !isGroup ? activeSocialProfile?.id : null,
        params: !isGroup
            ? {
                  period: 'custom',
                  with_labels: true,
                  start: startDate,
                  end: endDate,
                  filter_labels: albumInfo?.criteria?.labels ?? [],
                  filter_hashtags: albumInfo?.criteria?.hashtags ?? [],
                  filter_post_type: albumInfo?.criteria?.post_types ?? []
              }
            : null
    });

    const {
        dataStories: singleSPDataStories,
        isLoadingInitialData,
        isLoadingMore,
        isLoadMore,
        loadMoreStories,
        setRequestParams,
        mutateStories
    } = singleSPResult;

    useEffect(() => {
        if (!isGroup && setRequestParams) {
            setRequestParams((prevState) => ({
                ...prevState,
                start: startDate,
                end: endDate,
                filter_labels: albumInfo?.criteria?.labels ?? [],
                filter_hashtags: albumInfo?.criteria?.hashtags ?? [],
                filter_post_type: albumInfo?.criteria?.post_types ?? []
            }));
        }
    }, [
        albumInfo?.criteria?.started_at,
        albumInfo?.criteria?.ended_at,
        albumInfo?.criteria?.labels,
        albumInfo?.criteria?.hashtags,
        albumInfo?.criteria?.post_types,
        isGroup
    ]);

    // Use group stories for groups, single SP stories otherwise
    const dataStories = isGroup ? groupStoriesResult.dataStories : singleSPDataStories;
    const loading = isGroup ? groupStoriesResult.isLoadingInitialData : isLoadingInitialData;
    const loadingMore = isGroup ? groupStoriesResult.isLoadingMore : isLoadingMore;
    const hasMore = isGroup ? groupStoriesResult.isLoadMore : isLoadMore;
    const loadMore = isGroup ? groupStoriesResult.loadMoreStories : loadMoreStories;
    const mutate = isGroup ? groupStoriesResult.mutateStories : mutateStories;

    return (
        <>
            <MediaSelectionSection dataMedias={dataStories} />
            <PostsList
                dataMedias={dataStories}
                isLoadingInitialData={loading}
                isLoadingMore={loadingMore}
                isLoadMore={hasMore}
                loadMoreMedia={loadMore}
                mutateMedias={mutate}
            />
        </>
    );
};

export default StoriesListWrapper;
